import { EntityType, getEntityName } from "@ls/entity-service";
import { memoize } from "@ls/js-utils";
import { format, parseISO } from "date-fns";
import { createSelector } from "reselect";
import {
  selectScenarioEntityIds,
  selectScenarioOptions,
} from "state/scenario/selector";
import { GroupTypeEntity } from "state/selections/GroupType";
import { MajorMinorEntity } from "state/selections/MajorMinor";
import Mode, { ModeEntity } from "state/selections/Mode";
import SelectionState from "state/selections/SelectionState";

export const selectFetchingGroupingLevels = (state) =>
  state.Selections.fetchingGroupingLevels;
export const selectGroupingLevels = (state) => state.Selections.groupingLevels;

export const selectFetchingInvestPlatformId = (selectionState) => (state) =>
  state.Selections[selectionState].userInvestPlatformIds;
export const selectGroupingLevelOptions = createSelector(
  selectGroupingLevels,
  (groupingLevels = []) =>
    groupingLevels.map(({ id }) => ({ label: id, value: id })),
);

export const selectAllEntities = createSelector(
  (state) => state.Selections.entities,
  (entities) =>
    entities
      ?.map((entity) => ({
        ...entity,
        label: getEntityName(entity),
      }))
      ?.sort((e1, e2) => {
        if (e1.type === EntityType.BMARK && e2.type === EntityType.BMARK) {
          if (e1.label < e2.label) return -1;
          if (e1.label > e2.label) return 1;
        }
        if (e1.type === EntityType.BMARK) return 1;
        if (e2.type === EntityType.BMARK) return -1;
        if (e1.label < e2.label) return -1;
        if (e1.label > e2.label) return 1;
        return 0;
      }) ?? [],
);

export const selectAllEntitiesByEntityId = createSelector(
  selectAllEntities,
  (entities) =>
    entities?.reduce(
      (byEntityId, entity) => byEntityId.set(entity.entityId, entity),
      new Map(),
    ) ?? new Map(),
);
export const selectFetchingEntities = (state) =>
  state.Selections.fetchingEntities;

export const selectFetchingGroupsByGroupTypeMap = (state) =>
  state.Selections.fetchingGroupsByGroupTypeMap;

export const selectGroupsByGroupTypeMap = (state) =>
  state.Selections.groupsByGroupTypeMap;
export const selectSelectedEntityIds = (selectionState) => (state) =>
  state.Selections[selectionState].entityIds;
export const selectSelectedEntities = memoize((selectionState) =>
  createSelector(
    selectAllEntitiesByEntityId,
    selectSelectedEntityIds(selectionState),
    (entitiesByEntityId, entityIds) =>
      entityIds?.map((entityId) => entitiesByEntityId.get(entityId)) ?? [],
  ),
);
export const selectAsOfDateValue = (selectionState) => (state) =>
  state.Selections[selectionState].asOfDate;
export const selectSymbolicDate = (selectionState) => (state) =>
  state.Selections[selectionState].symbolicDate;
export const selectModeValue = (selectionState) => (state) =>
  state.Selections[selectionState].mode;
export const selectModeEntity = (selectionState) => (state) =>
  ModeEntity[selectModeValue(selectionState)(state)];

export const selectGroupTypeValue = (selectionState) => (state) =>
  state.Selections[selectionState].groupType;
export const selectDisplayBenchmarkValue = (selectionState) => (state) =>
  state.Selections[selectionState].displayBenchmarks;
export const selectBenchmarkEntityId = (selectionState) => (state) =>
  state.Selections[selectionState].benchmarkEntityId;
export const selectLookthruValue = (selectionState) => (state) =>
  state.Selections[selectionState].lookthru;
export const selectMajorMinorValue = (selectionState) => (state) =>
  state.Selections[selectionState].majorMinor;

export const selectUncommittedGroupTypeValue = (state) =>
  state.Selections[SelectionState.UNCOMMITTED].groupType;
export const selectCommittedGroupTypeValue = (state) =>
  state.Selections[SelectionState.COMMITTED].groupType;
export const selectUncommittedGroupValues = (state) =>
  state.Selections[SelectionState.UNCOMMITTED].groups;
export const selectCommittedGroupValues = (state) =>
  state.Selections[SelectionState.COMMITTED].groups;

export const selectScenarioValue = (selectionState) => (state) =>
  state.Selections[selectionState].scenario;

export const selectBenchmarkScenarioValue = (selectionState) => (state) =>
  state.Selections[selectionState].benchmarkScenario;

export const selectGlPamValue = (selectionState) => (state) =>
  state.Selections[selectionState].glPam;

export const selectScenarioEntity = memoize((selectionState) =>
  createSelector(
    selectScenarioValue(selectionState),
    selectScenarioOptions,
    (value, options) => {
      return options?.find((option) => option.value === value?.[0]);
    },
  ),
);
export const selectMajorMinorEntity = memoize((selectionState) =>
  createSelector(selectMajorMinorValue(selectionState), (value) =>
    value ? MajorMinorEntity[value] : null,
  ),
);
export const selectSideBySideValue = (selectionState) => (state) =>
  state.Selections[selectionState].sideBySide;

export const selectIsDisableBenchmarkWorkspace = (selectionState) => (state) =>
  state.Selections[selectionState].isDisableBenchmarkWorkspace;

/**********/

export const selectCommittedAsOfDateEntity = createSelector(
  selectAsOfDateValue(SelectionState.COMMITTED),
  (selectedAsOfDateValue) =>
    selectedAsOfDateValue
      ? parseISO(selectedAsOfDateValue)
      : selectedAsOfDateValue,
);

export const selectUncommittedGroupTypeEntity = createSelector(
  selectUncommittedGroupTypeValue,
  (selectedGroupTypeValue) => GroupTypeEntity[selectedGroupTypeValue],
);
export const selectCommittedGroupTypeEntity = createSelector(
  selectCommittedGroupTypeValue,
  (selectedGroupTypeValue) => GroupTypeEntity[selectedGroupTypeValue],
);

export const selectUncommittedAllGroupEntities = createSelector(
  selectUncommittedGroupTypeValue,
  selectGroupsByGroupTypeMap,
  (selectedGroupTypeValue, groupsByGroupTypeMap) =>
    groupsByGroupTypeMap[selectedGroupTypeValue] ?? [],
);
export const selectCommittedAllGroupEntities = createSelector(
  selectCommittedGroupTypeValue,
  selectGroupsByGroupTypeMap,
  (selectedGroupTypeValue, groupsByGroupTypeMap) =>
    groupsByGroupTypeMap[selectedGroupTypeValue] ?? [],
);

export const selectCommittedGroupEntities = createSelector(
  selectCommittedGroupTypeEntity,
  selectCommittedAllGroupEntities,
  selectCommittedGroupValues,
  (selectedGroupTypeEntity, groupEntities, selectedGroupValues) => {
    const res = [];
    if (selectedGroupValues) {
      selectedGroupValues.forEach((selectedGroupValue) => {
        const groupEntity = groupEntities.find(
          (groupEntity) =>
            groupEntity[selectedGroupTypeEntity.comboboxValueKey] ===
            selectedGroupValue,
        );
        res.push(groupEntity || { id: selectedGroupValue });
      });
    }
    return res;
  },
);

export const selectFetchingGroups = createSelector(
  selectUncommittedGroupTypeValue,
  selectFetchingGroupsByGroupTypeMap,
  (selectedGroupTypeValue, fetchingGroupsByGroupTypeMap) => {
    if (selectedGroupTypeValue) {
      return fetchingGroupsByGroupTypeMap[selectedGroupTypeValue] ?? false;
    }
    return false;
  },
);

/** ***************************************************** **/

export const selectUncommittedValues = createSelector(
  selectAsOfDateValue(SelectionState.UNCOMMITTED),
  selectUncommittedGroupTypeValue,
  selectUncommittedGroupValues,
  selectDisplayBenchmarkValue(SelectionState.UNCOMMITTED),
  selectMajorMinorValue(SelectionState.UNCOMMITTED),
  selectScenarioValue(SelectionState.UNCOMMITTED),
  selectSelectedEntityIds(SelectionState.UNCOMMITTED),
  selectSymbolicDate(SelectionState.UNCOMMITTED),
  (
    asOfDateValue,
    groupTypeValue,
    groupValue,
    displayBenchmarksValue,
    majorMinorValue,
    scenarioValue,
    selectedEntityIds,
    symbolicDate,
  ) => ({
    asOfDateValue,
    groupTypeValue,
    groupValue,
    displayBenchmarksValue,
    majorMinorValue,
    scenarioValue,
    selectedEntityIds,
    symbolicDate,
  }),
);
export const selectCommittedEntities = createSelector(
  selectCommittedAsOfDateEntity,
  selectCommittedGroupTypeEntity,
  selectCommittedGroupEntities,
  selectDisplayBenchmarkValue(SelectionState.COMMITTED),
  selectLookthruValue(SelectionState.COMMITTED),
  selectMajorMinorEntity(SelectionState.COMMITTED),
  selectModeEntity(SelectionState.COMMITTED),
  selectScenarioEntity(SelectionState.COMMITTED),
  selectSymbolicDate(SelectionState.COMMITTED),
  (
    asOfDateEntity,
    groupTypeEntity,
    groupEntities,
    displayBenchmarksValue,
    lookThru,
    majorMinorEntity,
    modeEntity,
    scenarioEntity,
    symbolicDate,
  ) => ({
    asOfDateEntity,
    groupTypeEntity,
    groupEntities,
    displayBenchmarksValue,
    lookThru,
    majorMinorEntity,
    modeEntity,
    scenarioEntity,
    symbolicDate,
  }),
);

export const selectGetBaseGroupingParams = createSelector(
  selectDisplayBenchmarkValue(SelectionState.COMMITTED),
  selectCommittedGroupTypeEntity,
  selectCommittedGroupValues,
  selectMajorMinorEntity(SelectionState.COMMITTED),
  selectCommittedAsOfDateEntity,
  selectModeValue(SelectionState.COMMITTED),
  selectSideBySideValue(SelectionState.COMMITTED),
  selectScenarioValue(SelectionState.COMMITTED),
  selectScenarioEntityIds,
  selectSelectedEntityIds(SelectionState.COMMITTED),
  selectBenchmarkEntityId(SelectionState.COMMITTED),
  selectLookthruValue(SelectionState.COMMITTED),
  selectIsDisableBenchmarkWorkspace(SelectionState.COMMITTED),
  selectBenchmarkScenarioValue(SelectionState.COMMITTED),
  selectScenarioOptions,
  (
    displayBenchmarksValue,
    groupTypeEntity,
    groupValues,
    majorMinorEntity,
    asOfDateEntity,
    modeValue,
    sideBySide,
    scenarioValue,
    scenarioEntityIds,
    selectedEntityIds,
    benchmarkEntityId,
    lookthru,
    isDisableBenchmarkWorkspace,
    benchmarkScenarioValue,
    scenarioOptions,
  ) => {
    return (extraParams = {}) => {
      const params = {
        appName: process.env.REACT_APP_ID,
        dates: asOfDateEntity ? format(asOfDateEntity, "yyyy-MM-dd") : null,
        major_minor_reporting: majorMinorEntity.groupingValue,
        meta_data: "NONE",
        results: ["VALUES_OBJECTS"],
        analytics: "NONE",
        show_references: false,
        lookthru: lookthru,
      };
      if (extraParams.summary) {
        params.summary = extraParams.summary;
      }
      if (modeValue === Mode.ACC_GROUPS) {
        params.group_type = groupTypeEntity?.groupingValue;
        params.ids = groupValues?.join(",");
      } else if (modeValue === Mode.SCENARIOS) {
        params.ids = Array.isArray(scenarioEntityIds)
          ? scenarioEntityIds
              ?.flat(1)
              .filter((i) => !!i && i !== undefined)
              .join(",") || "400"
          : (scenarioEntityIds ?? "400");
      } else if (modeValue === Mode.ENTITIES) {
        params.ids = selectedEntityIds?.join(",");
      }

      if (displayBenchmarksValue || extraParams.benchesToo) {
        params.benchesToo = true;
        if (benchmarkEntityId) {
          params.alt_bench_ids = benchmarkEntityId;
        }
      }

      if (scenarioValue && !sideBySide) {
        /*  const filteredScenarioArrayByUser = scenarioValue?.filter((f) =>
           scenarioOptions.some((item) => item.value === f)
         ); */
        const filterNullScenarios =
          Array.isArray(scenarioValue) &&
          scenarioValue.filter((i) => !!i && i !== undefined);
        params.workspace =
          filterNullScenarios?.length > 1
            ? `TRS_COMPOSITE(${filterNullScenarios?.join(",")})`
            : filterNullScenarios[0];
      }

      /* if (!!benchmarkScenarioValue && !isDisableBenchmarkWorkspace) {
        params.alt_bench_workspace = benchmarkScenarioValue
      } */

      if (!isDisableBenchmarkWorkspace) {
        params.alt_bench_workspace = !!benchmarkScenarioValue
          ? benchmarkScenarioValue
          : "";
      }
      if (extraParams?.analytics?.length) {
        params.analytics = extraParams.analytics;
      }

      if (extraParams.percentile_analytics?.length) {
        params.percentile_analytics = extraParams.percentile_analytics;
      }
      if (extraParams.percentiles?.length) {
        params.percentiles = extraParams.percentiles;
      }

      if (extraParams?.costBases?.length) {
        params.costBases = extraParams.costBases;
      }

      if (extraParams.quality_rating_modes) {
        params.quality_rating_modes = extraParams.quality_rating_modes;
      }

      if (extraParams.quality_rating_metrics) {
        params.quality_rating_metrics = extraParams.quality_rating_metrics;
      }

      if (extraParams.scenario_factors?.length) {
        params.scenario_factors = extraParams.scenario_factors;
      }
      if (extraParams.scenario_ids?.length) {
        params.scenario_ids = extraParams.scenario_ids;
      }
      if (extraParams.scenario_values?.length) {
        params.scenario_values = extraParams.scenario_values;
      }

      const sbsParams = (() => {
        if (scenarioValue && sideBySide) {
          const filterNullScenarios =
            Array.isArray(scenarioValue) &&
            scenarioValue.filter((i) => !!i && i !== undefined);
          return {
            ...params,
            workspace:
              filterNullScenarios?.length > 1
                ? `TRS_COMPOSITE(${filterNullScenarios?.join(",")})`
                : filterNullScenarios[0],
          };
        }
      })();
      return [params, sbsParams];
    };
  },
);

export const selectBaseGroupingParams = createSelector(
  selectGetBaseGroupingParams,
  (getBaseGroupingParams) => {
    return getBaseGroupingParams();
  },
);

export const selectUserInvestPlatformIds = (selectionState) => (state) =>
  state.Selections[selectionState].userInvestPlatformIds;
